import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Waves, Wind, Smile, Sunset, Calendar, MapPin, Home, Coffee, CheckCircle2 } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function ResetRetreatPage() {
  return <div dir="rtl" className="font-['Heebo'] bg-slate-50 text-slate-800 min-h-screen overflow-x-hidden" data-id="element-1436" data-mp="jf861nic996m">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden" data-id="element-1437" data-mp="5eoysilp675i">
        <div className="absolute inset-0 z-0" data-id="element-1438" data-mp="acqi94n1c8hi">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-1439" data-mp="2q5phevfuj0pb">
            <source src="https://cdn.magicpatterns.com/uploads/aAuWxGLVcEb5PCo3LzHw7T/VIDEO-2026-04-21-10-42-44.mp4" type="video/mp4" data-id="element-1440" data-mp="2v4iw2r6vcxr2" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" data-id="element-1441" data-mp="vdpldzeist05"></div>
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-16" data-id="element-1442" data-mp="qfnz75lssdkg">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} data-id="element-1443" data-mp="15bvnah0btz50">
            <span className="inline-block py-1 px-3 rounded-full bg-teal-500/20 text-teal-200 border border-teal-500/30 text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1444" data-mp="1ea7jqznvjr5d">
              4-6 ביוני, 2026 | חוף מעגן, כנרת
            </span>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight" data-id="element-1445" data-mp="1o3g0aanz7yxb">
              פשוט להיות. <br data-id="element-1446" data-mp="1x29s7nqd414j" />
              <span className="text-teal-400" data-id="element-1447" data-mp="1x1rz2zyzy30u">ריטריט RESET</span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-200 mb-10 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-1448" data-mp="1t0ol6j0ieh29">
              שלושה ימים של ניתוק מהרעש וחיבור לשקט, לכנרת, לגוף ולקהילה.
            </p>
          </motion.div>
        </div>
        {/* Scroll indicator */}
        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2
      }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 z-10" data-id="element-1449" data-mp="25391linwiw52">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center p-1" data-id="element-1450" data-mp="2euvnc8xv8dm2">
            <div className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-1451" data-mp="2iwgtlbfw9tna"></div>
          </div>
        </motion.div>
      </section>

      {/* What Awaits Us Section */}
      <section className="py-24 px-4 bg-white" data-id="element-1452" data-mp="2stlktpolkfay">
        <div className="max-w-6xl mx-auto" data-id="element-1453" data-mp="2sqpmi08u7olz">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-1454" data-mp="2xqlppx9pnc0h">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1455" data-mp="13d3zg2hx811r">
              מה מחכה לנו במים (ובחוף)?
            </h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto rounded-full" data-id="element-1456" data-mp="3gpgfsesv5dgj"></div>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1457" data-mp="3bvm2pu7rjwqf">
            {[{
            image: 'https://cdn.magicpatterns.com/uploads/sZvN5qMq5z1g4itY3TLS7p/450c666a-127e-4210-8e53-b50e5ae8ca31.jpg',
            title: 'סאפ וחתירה',
            desc: 'חתירות בוקר בראי של הכנרת, שיפור טכניקה ועבודה על יציבות.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/iB2BDqYpRmo1UfxAEHupva/7a450878-29f4-4003-be04-e530e563c8cd.jpg',
            title: 'ווינג-פויל (Wing Foil)',
            desc: 'הדרכת היכרות עם הלהיט החדש של עולם המים – תחושת תעופה מעל המים.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/bbWSuM5Yyx2KKeVhp6v4TC/986ca47d-8d42-4eda-a1fa-b9a784920765.jpg',
            title: 'יוגלאטיס ותנועה',
            desc: 'תרגול מול הנוף הפתוח לחיזוק הגוף והרגעת הנפש.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/9RhaMzBe737i8zTZnvdDuM/IMG_8179.jpg',
            title: 'זמן חוף ושקט',
            desc: 'מעגלי שיח בערב מול הנוף וזמן חופשי לעצמך.'
          }].map((feature, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-slate-50 p-8 rounded-3xl text-center hover:shadow-xl transition-shadow border border-slate-100 flex flex-col items-center" data-id="element-1458" data-mp="3u0tj2aods23x" data-mpi={idx}>
                <div className="w-full h-48 rounded-2xl overflow-hidden mb-6 shadow-sm" data-id="element-1459" data-mp="pmljptdm9r2v">
                  <img src={feature.image} alt={feature.title} className="w-full h-full object-cover" data-id="element-1460" data-mp="boolytnffemm" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-800" data-id="element-1461" data-mp="gpud4n1futu1">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1462" data-mp="31erz3wzxiw05">{feature.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-12 bg-slate-900 overflow-hidden" data-id="element-1463" data-mp="2ovwkohlr0ndd">
        <div className="flex gap-4 px-4 overflow-x-auto pb-8 snap-x snap-mandatory scrollbar-hide" data-id="element-1464" data-mp="2ovczms0sxiv4">
          {['https://cdn.magicpatterns.com/uploads/euveAznXLyoRgqWzJJM5d2/c5ba1ee6-439d-47d1-9f37-d431e61bd061.jpg', 'https://cdn.magicpatterns.com/uploads/q9hdMEFoFD2zoRq7GwyTmt/4e20683c-8dc6-493a-a623-9274c19ff803.jpg', 'https://cdn.magicpatterns.com/uploads/5GQjcjiGrdz6DgD6rNz6Xx/e74f9f3c-a78a-45c4-8dea-b0c7c0f86bca.jpg'].map((img, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.2
        }} className="min-w-[80vw] md:min-w-[400px] h-[300px] md:h-[400px] snap-center rounded-2xl overflow-hidden relative group" data-id="element-1465" data-mp="2jw9yvjrutysy" data-mpi={idx}>
              <img src={img} alt={`Retreat moment ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-1466" data-mp="3mjf3x9zmmu3j" />
              <div className="absolute inset-0 bg-teal-900/20 group-hover:bg-transparent transition-colors duration-500" data-id="element-1467" data-mp="3rgxef4talhdn"></div>
            </motion.div>)}
        </div>
      </section>

      {/* Schedule Section */}
      <section className="py-24 px-4 bg-slate-50 relative overflow-hidden" data-id="element-1468" data-mp="52483ezppkyu">
        <div className="absolute top-40 -left-40 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-50" data-id="element-1469" data-mp="33lup6vqktbwn"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-1470" data-mp="38k5y9491ridt">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-1471" data-mp="3di7gjmynfdo8">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1472" data-mp="3hi7ul6hu5mur">
              לוח זמנים מקוצר
            </h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto rounded-full mb-6" data-id="element-1473" data-mp="wttl2xq87gva"></div>
            <p className="text-slate-600 text-lg" data-id="element-1474" data-mp="wvxyq7dxfu8r">
              שלושה ימים מלאים בחוויות, מתוכננים בקפידה.
            </p>
          </motion.div>

          <div className="relative" data-id="element-1475" data-mp="2dblldlrt3b6x">
            <div className="absolute right-4 md:right-1/2 top-0 bottom-0 w-1 bg-teal-100 transform md:translate-x-0.5" data-id="element-1476" data-mp="2dcn9aqtx7s62"></div>
            <div className="space-y-12" data-id="element-1477" data-mp="23kqx6bv021iu">
              {[{
              day: 'יום חמישי',
              date: '4.6',
              title: 'הגעה והתאקלמות',
              content: 'התכנסות בצהריים, חתירת היכרות בשקיעה וארוחת ערב משותפת.',
              icon: Sunset
            }, {
              day: 'יום שישי',
              date: '5.6',
              title: 'מעמיקים פנימה',
              content: 'חתירת זריחה, סדנת יוגלאטיס, סדנאות טכניקת חתירה, היכרות עם ווינג-פויל וקבלת שבת.',
              icon: Waves
            }, {
              day: 'יום שבת',
              date: '6.6',
              title: 'סיכום ופרידה',
              content: 'תרגול בוקר, חתירה חגיגית לסיכום ופרידה.',
              icon: Smile
            }].map((item, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              delay: idx * 0.2
            }} className={`relative flex flex-col md:flex-row items-start md:items-center gap-8 ${idx % 2 === 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-1478" data-mp="2nvlbnzhighw3" data-mpi={idx}>
                  <div className="absolute right-0 md:right-1/2 transform translate-x-3.5 md:translate-x-6 w-10 h-10 bg-white border-4 border-teal-400 rounded-full flex items-center justify-center z-10 shadow-md" data-id="element-1479" data-mp="295ntq1a6cofc">
                    <div className="w-2.5 h-2.5 bg-teal-500 rounded-full" data-id="element-1480" data-mp="204ps1pn7kszx"></div>
                  </div>

                  <div className={`w-full md:w-1/2 pl-12 pr-12 md:px-12 flex ${idx % 2 === 0 ? 'md:justify-start' : 'md:justify-end'}`} data-id="element-1481" data-mp="3pje23k8btyus">
                    <div className="text-right md:text-center" data-id="element-1482" data-mp="3uigv26b18eme">
                      <span className="inline-block px-4 py-1.5 bg-teal-50 text-teal-700 rounded-full font-bold text-sm mb-2 border border-teal-100" data-id="element-1483" data-mp="1iv3tfqvy0cye">
                        {item.date}
                      </span>
                      <h3 className="text-2xl font-extrabold text-slate-800" data-id="element-1484" data-mp="1smi14ly1sj2m">
                        {item.day}
                      </h3>
                    </div>
                  </div>

                  <div className="w-full md:w-1/2 pl-4 pr-12 md:px-12" data-id="element-1485" data-mp="21lbu82c66rbf">
                    <div className="bg-white rounded-2xl p-6 shadow-lg shadow-slate-200/50 border border-slate-100 hover:-translate-y-1 transition-transform duration-300" data-id="element-1486" data-mp="1wn1wpl2z3emn">
                      <div className="mb-3" data-id="element-1487" data-mp="2bi85gftgdrjo">
                        <h4 className="text-xl font-bold text-slate-800" data-id="element-1488" data-mp="26ky8mf3qidlt">
                          {item.title}
                        </h4>
                      </div>
                      <p className="text-slate-600 leading-relaxed" data-id="element-1489" data-mp="c76q0q3fgje8">
                        {item.content}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details & Pricing */}
      <section id="register" className="py-24 px-4 bg-white relative overflow-hidden" data-id="element-1490" data-mp="1ifjdih50ceuf">
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-50" data-id="element-1491" data-mp="1ihck5wfmez2f"></div>
        <div className="max-w-6xl mx-auto relative z-10" data-id="element-1492" data-mp="2b4gjplvy2l6t">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-20" data-id="element-1493" data-mp="2b61bj3qvjq0z">
            <div className="text-center mb-12" data-id="element-1494" data-mp="1wfajpvpa8qxv">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1495" data-mp="1wej1rnolyrq2">
                פרטים טכניים
              </h2>
              <div className="w-24 h-1 bg-teal-500 mx-auto rounded-full" data-id="element-1496" data-mp="2twrh507txqxj"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1497" data-mp="2p1vd11pbjd42">
              <div className="bg-slate-50 rounded-3xl border border-slate-100 text-center hover:shadow-lg transition-shadow overflow-hidden flex flex-col" data-id="element-1498" data-mp="2xdxb5tqjq0w1">
                <div className="h-48 w-full relative" data-id="element-1499" data-mp="36dk3zr57lydh">
                  <img src="https://cdn.magicpatterns.com/uploads/gaKJFduspmT4WMqMsV6Gs9/ריזורט-אפשר-גם-לגלריה-אם-חסר.jpg" alt="Sea of Galilee" className="w-full h-full object-cover" data-id="element-1500" data-mp="36bpk8g5occaz" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" data-id="element-1501" data-mp="3gaot9zucq9yi"></div>
                </div>
                <div className="p-8 pt-6 flex-1" data-id="element-1502" data-mp="3kayuz0b42e7k">
                  <h3 className="text-xl font-bold text-slate-800 mb-3" data-id="element-1503" data-mp="3p3iai2tczjf0">
                    מיקום
                  </h3>
                  <p className="text-slate-600" data-id="element-1504" data-mp="3u2d31wh4omcb">
                    חוף מעגן, כנרת. חוף פרטי ושקט.
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-3xl border border-slate-100 text-center hover:shadow-lg transition-shadow overflow-hidden flex flex-col" data-id="element-1505" data-mp="7v54bd0prdp9">
                <div className="h-48 w-full relative" data-id="element-1506" data-mp="3easu3oqnqp4h">
                  <img src="https://cdn.magicpatterns.com/uploads/awHjGHHpAWW4fTgQ7Pwa9Y/חדר_משפחתי.jpg" alt="Family Room" className="w-full h-full object-cover" data-id="element-1507" data-mp="39elnruucnbm0" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" data-id="element-1508" data-mp="3jc031r4jducq"></div>
                </div>
                <div className="p-8 pt-6 flex-1" data-id="element-1509" data-mp="x7k4bg19jh4">
                  <h3 className="text-xl font-bold text-slate-800 mb-3" data-id="element-1510" data-mp="103j833mae75">
                    לינה
                  </h3>
                  <p className="text-slate-600" data-id="element-1511" data-mp="aw6npflxyox9">
                    <a href="https://www.maagan.co.il/" target="_blank" rel="noopener noreferrer" className="text-teal-600 font-medium hover:text-teal-700 underline underline-offset-4 decoration-teal-600/30 hover:decoration-teal-600 transition-all" data-id="element-1512" data-mp="auveh09q8oyr">
                      כפר הנופש, מעגן עדן
                    </a>{' '}
                    בקיבוץ מעגן. חדרים משפחתיים, מרווחים ומפנקים שמתאימים לשלושה
                    מבוגרים.
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-3xl border border-slate-100 text-center hover:shadow-lg transition-shadow overflow-hidden flex flex-col" data-id="element-1513" data-mp="s47iey5069o9">
                <div className="h-48 w-full relative" data-id="element-1514" data-mp="iffur26z6bj5">
                  <img src="https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?auto=format&fit=crop&q=80&w=800" alt="Rich Breakfast" className="w-full h-full object-cover" data-id="element-1515" data-mp="ie4nuti8gnnt" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" data-id="element-1516" data-mp="8ib6mwtkk5zp"></div>
                </div>
                <div className="p-8 pt-6 flex-1" data-id="element-1517" data-mp="3qp924bdkt2bl">
                  <h3 className="text-xl font-bold text-slate-800 mb-3" data-id="element-1518" data-mp="3vlfypdk311gv">
                    תזונה
                  </h3>
                  <p className="text-slate-600" data-id="element-1519" data-mp="1ox2wb1zbyz5b">
                    ארוחות בריאות ומזינות לאורך כל הריטריט: בוקר וערב.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Pricing Section */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="mt-24" data-id="element-1520" data-mp="3kz62jkbpncjw">
            <div className="text-center mb-12" data-id="element-1521" data-mp="3bxyfjyen9y54">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1522" data-mp="36zojlll3itig">
                עלויות והרשמה
              </h2>
              <p className="text-slate-600" data-id="element-1523" data-mp="327mzxellm0ie">
                מספר המקומות מוגבל, הבטיחו את מקומכם מראש.
              </p>
            </div>

            <div className="max-w-3xl mx-auto" data-id="element-1524" data-mp="2x6z8wsxddjo6">
              <div className="relative rounded-3xl border border-teal-500/30 shadow-2xl shadow-teal-900/20 flex flex-col overflow-hidden text-center" data-id="element-1525" data-mp="2t8rrrwyzugcg">
                <div className="absolute inset-0 z-0" data-id="element-1526" data-mp="2oahwznh6lhrd">
                  <img src="https://cdn.magicpatterns.com/uploads/idDXetNMa2tFPXNmSZmXFC/pool-2.jpg" alt="Resort Pool View" className="w-full h-full object-cover" data-id="element-1527" data-mp="2javilapm9vgz" />
                  <div className="absolute inset-0 bg-slate-900/85 backdrop-blur-[2px]" data-id="element-1528" data-mp="13q3dfmwzxau5"></div>
                </div>
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-teal-400 to-blue-500 z-20" data-id="element-1529" data-mp="18pfzw6jygfw7"></div>

                <div className="relative z-10 p-8 md:p-12 flex flex-col h-full" data-id="element-1530" data-mp="1cpzscwtl2vc0">
                  <div className="mb-8" data-id="element-1531" data-mp="g23c5t22621b">
                    <h3 className="text-4xl md:text-5xl font-extrabold text-white mb-3 drop-shadow-md" data-id="element-1532" data-mp="fyxngexcd264">
                      הרשמה מוקדמת
                    </h3>
                    <div className="inline-block bg-teal-500/20 border border-teal-400/30 text-teal-300 text-sm font-bold px-4 py-1.5 rounded-full backdrop-blur-sm" data-id="element-1533" data-mp="p0nma5tjz3dx">
                      בתוקף עד ה-10.5.2026
                    </div>
                  </div>

                  <p className="text-sm text-teal-200/70 mb-10 px-4 max-w-lg mx-auto" data-id="element-1534" data-mp="1c4d7o2oimks">
                    הלינה בחדרי פרמיום גדולים ומרווחים. המחיר משתנה לפי מספר
                    המשתתפים בחדר.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-1535" data-mp="zoqk3s0yv65p">
                    <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 backdrop-blur-sm" data-id="element-1536" data-mp="1eclb93zbt2go">
                      <div className="text-teal-200/80 text-lg font-medium mb-3" data-id="element-1537" data-mp="19di67jjciw8p">
                        מחיר לאדם (זוג בחדר)
                      </div>
                      <div className="flex items-baseline justify-center gap-3" data-id="element-1538" data-mp="lqa3n9day1ad">
                        <span className="text-5xl font-extrabold text-teal-400" data-id="element-1539" data-mp="gqnrl397mr35">
                          ₪2,950
                        </span>
                        <span className="text-2xl text-slate-500 line-through decoration-slate-500/50" data-id="element-1540" data-mp="qnijh3az9t8g">
                          ₪3,200
                        </span>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 backdrop-blur-sm" data-id="element-1541" data-mp="22377zz3vr0bd">
                      <div className="text-teal-200/80 text-lg font-medium mb-3" data-id="element-1542" data-mp="29l5fxz6ey3ch">
                        מחיר לאדם (שלישייה בחדר)
                      </div>
                      <div className="flex items-baseline justify-center gap-3" data-id="element-1543" data-mp="1zo0phn5jblpv">
                        <span className="text-5xl font-extrabold text-teal-400" data-id="element-1544" data-mp="1zq55h3gpkb6i">
                          ₪2,450
                        </span>
                        <span className="text-2xl text-slate-500 line-through decoration-slate-500/50" data-id="element-1545" data-mp="2sdhfke5omqix">
                          ₪2,700
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900/40 rounded-2xl p-6 md:p-8 mb-10 text-right backdrop-blur-sm border border-slate-800/50" data-id="element-1546" data-mp="2ikk3oqv73zta">
                    <h4 className="text-white font-bold mb-4 text-lg border-b border-slate-700 pb-2" data-id="element-1547" data-mp="3basc3qzxj5uo">
                      המחיר כולל:
                    </h4>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1548" data-mp="3g43oe7nigetz">
                      {['כל הסדנאות והפעילויות', 'השכרת סאפ, משוט וחגורת הצלה לכל האירוע', 'ארוחות בוקר וערב', 'בריכת שחייה וחוף רחצה', 'פינת קפה מפנקת לאורך כל האירוע'].map((item, i) => <li key={i} className="flex items-start gap-3 text-white" data-id="element-1549" data-mp="176xrkonxn14p" data-mpi={i}>
                          <CheckCircle2 size={20} className="text-teal-400 shrink-0 mt-0.5" data-id="element-1550" data-mp="128mjojh770p4" />
                          <span className="leading-tight font-medium" data-id="element-1551" data-mp="1yyn98kpe5l7h">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                    <a href="https://wa.me/972548800260" target="_blank" rel="noopener noreferrer" className="w-full bg-slate-800 hover:bg-slate-900 text-white text-lg font-bold py-4 rounded-xl transition-colors shadow-md text-center block mt-6" data-id="element-1552" data-mp="1pxyrbewvn0pb">
                      להרשמה ותשלום כתבו לנו בווטסאפ
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12 text-center border-t border-slate-800" data-id="element-1553" data-mp="3vmzffxcxrycu">
        <div className="max-w-4xl mx-auto px-4" data-id="element-1554" data-mp="4f4vs3yssagk">
          <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1555" data-mp="9des4hw9dh3r">Retreat RESET</h2>
          <p className="mb-6" data-id="element-1556" data-mp="e4of1aqfa6by">פשוט להיות.</p>
          <div className="flex justify-center gap-6 mb-8" data-id="element-1557" data-mp="e5g7wdsmxpyj">
            <span className="flex items-center gap-2" data-id="element-1558" data-mp="i6i8axyag823">
              <Calendar size={18} data-id="element-1559" data-mp="i605tjl18xlq" /> 4-6 ביוני, 2026
            </span>
            <span className="flex items-center gap-2" data-id="element-1560" data-mp="n4bgtxn2de5j">
              <MapPin size={18} data-id="element-1561" data-mp="s4fy3pvsqcwt" /> חוף מעגן
            </span>
          </div>
          <p className="text-sm opacity-60" data-id="element-1562" data-mp="s57l4cbbv2bh">© 2026 כל הזכויות שמורות.</p>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/972548800260" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 left-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300 flex items-center justify-center group" aria-label="Contact on WhatsApp" data-id="element-1563" data-mp="2tw2iv9p3hbku">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-1564" data-mp="1aumvnvkuw3w6">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-1565" data-mp="1ft6ma9aea3uv" />
        </svg>
        <span className="absolute right-full mr-4 bg-white text-slate-800 px-3 py-1.5 rounded-lg text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-md pointer-events-none" data-id="element-1566" data-mp="3j0x6cqbhlodi">
          יש לכם שאלות? דברו איתי
        </span>
      </a>
    </div>;
}